import { useEffect, useState } from 'react';

/**
 * Minimum stage height (in px) the surface needs to render usefully: half
 * the height is ground (`sceneGeometry` in `utils/surfaceScene.ts`), and
 * `BuildingTile`'s sprites plus `ZoneStrip`'s dock-hugging strip need
 * headroom inside that band. Below this, tiles and the strip start
 * overlapping or clipping against the dock — a landscape phone (e.g. an
 * iPhone SE at 375×320 available) is the shape that hits this, not just a
 * hypothetical.
 */
export const MIN_SURFACE_STAGE_HEIGHT_PX = 320;

/**
 * Whether the surface stage has enough vertical room to render usefully,
 * given the space `AppShell`'s fixed HUD/dock chrome already claims. Used by
 * `PlanetPage` to fall back to the zone list rather than shipping a broken,
 * clipped stage (DS-18 scope item 4).
 */
export function useSurfaceFits(reservedChromePx: number): boolean {
    const [fits, setFits] = useState(() => window.innerHeight - reservedChromePx >= MIN_SURFACE_STAGE_HEIGHT_PX);

    useEffect(() => {
        const evaluate = () => setFits(window.innerHeight - reservedChromePx >= MIN_SURFACE_STAGE_HEIGHT_PX);
        evaluate();
        window.addEventListener('resize', evaluate);
        return () => window.removeEventListener('resize', evaluate);
    }, [reservedChromePx]);

    return fits;
}

export default useSurfaceFits;
